import { format as dateFnsFormat } from "date-fns";

/**
 * Format a date string or Date object to a localized date string
 * @param date - Date string or Date object
 * @returns Formatted date string (e.g., "May 1, 2026")
 */
export const formatDate = (date: string | Date): string => {
  return dateFnsFormat(new Date(date), "MMM d, yyyy");
};

/**
 * Format a date string or Date object with a custom format
 * @param date - Date string or Date object
 * @param formatString - Format string (e.g., "MMM dd, yyyy")
 * @returns Formatted date string
 */
export const formatDateCustom = (
  date: string | Date,
  formatString: string = "MMM dd, yyyy"
): string => {
  return dateFnsFormat(new Date(date), formatString);
};

/**
 * Format a date for display in auction cards
 * @param date - Date string or Date object
 * @returns Formatted date string (e.g., "May 1, 2026")
 */
export const formatAuctionDate = (date: string | Date): string => {
  // If date is a string in format YYYY-MM-DDTHH:mm:ssZ, extract just the date part
  // to avoid timezone conversion issues
  if (typeof date === 'string' && date.includes('T')) {
    const datePart = date.split('T')[0]; // Get "YYYY-MM-DD"
    const [year, month, day] = datePart.split('-');
    // Create date in local timezone to avoid UTC conversion
    return formatDateCustom(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)), "MMM d, yyyy");
  }
  return formatDateCustom(date, "MMM d, yyyy");
};

/**
 * Format a date and time with AM/PM format for timestamps
 * @param date - Date string or Date object
 * @returns Formatted date/time string (e.g., "May 18, 2026 7:57 PM")
 */
export const formatDateTimeWithAMPM = (date: string | Date): string => {
  return dateFnsFormat(new Date(date), "MMM d, yyyy h:mm a");
};

/**
 * Format a date and time with full details (ordinal day)
 * @param date - Date string or Date object
 * @returns Formatted date/time string (e.g., "May 18th, 2026 7:57 PM")
 */
export const formatDateTimeFull = (date: string | Date): string => {
  return dateFnsFormat(new Date(date), "PPP p");
};

/**
 * Convert 24-hour time format to 12-hour AM/PM format
 * @param time24 - Time string in 24-hour format (e.g., "21:46", "09:30")
 * @returns Formatted time string with AM/PM (e.g., "9:46 PM", "9:30 AM")
 */
export const formatTime12Hour = (time24: string): string => {
  if (!time24) return time24;
  
  const [hoursStr, minutes] = time24.split(':');
  const hours = parseInt(hoursStr, 10);
  
  if (isNaN(hours)) return time24;
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
  
  return `${hours12}:${minutes} ${period}`;
};

/**
 * Format a Date object for HTML date input (YYYY-MM-DD)
 * Uses local timezone to avoid timezone conversion issues
 * @param date - Date object
 * @returns Formatted date string (e.g., "2026-05-23")
 */
export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format an ISO datetime string to local time with date and time
 * @param dateTimeString - ISO datetime string (e.g., "2026-05-24T08:40:00Z" or "2026-05-24 08:40:00")
 * @returns Formatted local datetime string (e.g., "May 24, 2026 11:40 AM")
 */
export const formatToLocalDateTime = (dateTimeString: string): string => {
  if (!dateTimeString) return dateTimeString;
  
  // Parse the date string
  // If it has 'Z', it's UTC and will be converted to local
  // If it doesn't have 'Z' or timezone offset, JavaScript treats it as local already
  const date = new Date(dateTimeString);
  
  if (isNaN(date.getTime())) return dateTimeString;
  
  // Use date-fns to format with AM/PM
  return dateFnsFormat(date, 'MMM d, yyyy h:mm a');
};
